import React, { useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { ToastContainer } from 'react-toastify';
import { getParameterFromURL } from './utils/scripts';
import checkGeo from './utils/scripts/checkGeo';
import { RootState, useAppDispatch } from './store';
import saveABTestValue from './utils/scripts/saveABTestValue';
import { determinePaymentVersion } from './utils/scripts/paymentVersion';
import { handlePaypalRedirect } from './utils/payments/handlePaypalRedirect';
import AbTests from './abtests/AbTests';

// Common components
import SuccessScreen from '~/src/components/common/successScreenV2';
import Loader from '~/src/components/common/loader';
import Alert from '~/src/components/common/alert';
import { PDFUpselling } from '~/src/components/common/pdfUpselling';
import { PDFUpsellingV1p20 } from '~/src/components/common/pdfUpsellingV1p20';
import { PDFUpsellingK9 } from '~/src/components/common/pdfUpsellingK9';
import { ChatUpsale } from '~/src/components/common/chatUpsaleV2';
import { PaymentModalDiscount } from '~/src/components/common/modalDiscount';
import { FakeHardwar } from '~/src/components/common/fakeHardware';
import { SupplementsUpsale } from '~/src/components/common/supplementsUpsale';

function ViewportSizes() {
  function setupViewportHandler() {
    const viewport = window.visualViewport;
    const layoutViewport = document.querySelector('.layoutViewport');

    // https://developer.mozilla.org/en-US/docs/Web/API/VisualViewport
    function viewportHandler() {
      // Since the bar is position: fixed we need to offset it by the visual
      // viewport's offset from the layout viewport origin.
      const { offsetLeft } = viewport;
      const offsetTop = viewport.height - layoutViewport.getBoundingClientRect().height + viewport.offsetTop;

      // You could also do this by setting style.left and style.top if you
      // use width: 100% instead.
      document.documentElement.style.setProperty('--offsetTop', String(offsetTop));
      document.documentElement.style.setProperty('--offsetLeft', String(offsetLeft));
      document.documentElement.style.setProperty('--viewPortScale', String(viewport.scale));
      document.documentElement.style.setProperty('--viewportOffsetTop', String(viewport.offsetTop));
    }

    window.visualViewport.addEventListener('scroll', viewportHandler);
    window.visualViewport.addEventListener('resize', viewportHandler);
  }

  useEffect(() => {
    setupViewportHandler();
    checkGeo();
  }, []);

  return <div className="layoutViewport" />;
}

function App() {
  const dispatch = useAppDispatch();
  const showLoader = useSelector((state: RootState) => state.navigation.showLoader);
  const purchaseFlowScreen = useSelector((state: RootState) => state.navigation.purchaseFlowScreen);
  const showModalDiscount = useSelector((state: RootState) => state.navigation.showModalDiscount);
  const v1p20PDFPaywall = getParameterFromURL('v1p20PDFPaywall') !== null;

  const v1p20PDFPaywallAB = useMemo(() => {
    if (!v1p20PDFPaywall || !purchaseFlowScreen.showPDFUpselling) {
      return null;
    }
    return saveABTestValue(2, '1p20_PDF_paywall', 'A', 'B', 50, 50);
  }, [v1p20PDFPaywall, purchaseFlowScreen.showPDFUpselling]);

  const showPDFUpsellingV1p20 = v1p20PDFPaywallAB === '1p20_PDF_paywall_B';

  const k9Funnel = useSelector((state: RootState) => state.data.k9Funnel);

  // K9 p20 links (v76p20/v78p20/v79p20) get the K9 guide upsell — rolled out from
  // group B of the `p20v76_PDF_regular_K9` test.
  const showPDFUpsellingK9 = k9Funnel && determinePaymentVersion() === 'p20';

  const renderPDFUpselling = () => {
    if (showPDFUpsellingK9) {
      return <PDFUpsellingK9 />;
    }
    if (showPDFUpsellingV1p20) {
      return <PDFUpsellingV1p20 />;
    }
    return <PDFUpselling />;
  };

  const paymentModalData = useSelector((state: RootState) => state.data.paymentModal);
  const paypalRedirectHandledRef = useRef(false);

  const productName = paymentModalData?.productName?.name || '';
  const productAmount = paymentModalData?.product?.details?.trial_amount || paymentModalData?.product?.details?.amount || '';

  if (getParameterFromURL('darkTheme') === 'true') {
    document.body.classList.add('dark');
  }

  useEffect(() => {
    handlePaypalRedirect({
      dispatch,
      productName,
      productAmount,
      handledRef: paypalRedirectHandledRef,
    });
  }, [dispatch, productAmount, productName]);

  return (
    <>
      <ViewportSizes />
      <AbTests />
      {showLoader && <Loader />}
      {purchaseFlowScreen.showSuccessScreen && <SuccessScreen email={purchaseFlowScreen.email} url={purchaseFlowScreen.url} upselPDF={purchaseFlowScreen.upselPDF} />}
      {purchaseFlowScreen.showPDFUpselling ? renderPDFUpselling() : null }
      {purchaseFlowScreen.showChatUpsale ? <ChatUpsale /> : null }
      {purchaseFlowScreen.showFakeHardwar ? <FakeHardwar /> : null }
      {purchaseFlowScreen.showSupplementsUpsale ? <SupplementsUpsale /> : null }
      {showModalDiscount ? <PaymentModalDiscount showModalDiscount={showModalDiscount} /> : null}
      <ToastContainer />
      <Alert />
    </>
  );
}

export default App;
